import React, { useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import { FaLock, FaEye, FaEyeSlash, FaCheck, FaTimes, FaArrowLeft } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { authService } from '../../services/authService';
import { setCredentials } from '../../redux/slices/authSlice';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { resetToken } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Password rules validation
  const rules = {
    length: password.length >= 8,
    uppercase: /[A-Z]/.test(password),
    lowercase: /[a-z]/.test(password),
    number: /\d/.test(password),
    special: /[@$!%*?&]/.test(password),
  };

  const isPasswordStrong = Object.values(rules).every(Boolean);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!password || !confirmPassword) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!isPasswordStrong) {
      toast.error('Password does not meet strength requirements');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const response = await authService.resetPassword(resetToken, password);
      if (response.success) {
        toast.success('Password reset successfully!');
        
        // Auto-login on success
        if (response.data && response.data.token) {
          dispatch(setCredentials({ user: response.data, token: response.data.token }));
          toast.success('Successfully logged in!');
          navigate('/admin');
        } else {
          // Fallback if data is not returned
          navigate('/admin/secure-login');
        }
      }
    } catch (error) {
      console.error('Reset password error:', error);
      toast.error(error.response?.data?.message || 'Token is invalid or has expired');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-12 px-4 sm:px-6 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8"
      >
        {/* Back to Login link */}
        <div>
          <Link
            to="/admin/secure-login"
            className="inline-flex items-center text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 gap-2 transition"
          >
            <FaArrowLeft className="text-xs" /> Back to Sign In
          </Link>
        </div>

        {/* Title */}
        <div className="text-center">
          <div className="text-3xl font-bold mb-2">
            <span className="gradient-text">Reset</span>
            <span className="text-gray-800 dark:text-white"> Password</span>
          </div>
          <p className="text-gray-600 dark:text-gray-400">Choose a strong new password for your account</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="text-gray-400" />
              </div>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="input-field pl-10 pr-10"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showPassword ? <FaEyeSlash className="text-gray-400" /> : <FaEye className="text-gray-400" />}
              </button>
            </div>

            {/* Dynamic Password Strength List */}
            {password.length > 0 && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="mt-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg space-y-1.5 text-xs text-gray-600 dark:text-gray-400"
              >
                <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">Password Strength Requirements:</p>
                <div className="flex items-center gap-2">
                  {rules.length ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />}
                  <span className={rules.length ? "text-green-600 dark:text-green-400" : ""}>At least 8 characters</span>
                </div>
                <div className="flex items-center gap-2">
                  {rules.uppercase ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />}
                  <span className={rules.uppercase ? "text-green-600 dark:text-green-400" : ""}>At least 1 uppercase letter (A-Z)</span>
                </div>
                <div className="flex items-center gap-2">
                  {rules.lowercase ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />}
                  <span className={rules.lowercase ? "text-green-600 dark:text-green-400" : ""}>At least 1 lowercase letter (a-z)</span>
                </div>
                <div className="flex items-center gap-2">
                  {rules.number ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />}
                  <span className={rules.number ? "text-green-600 dark:text-green-400" : ""}>At least 1 number (0-9)</span>
                </div>
                <div className="flex items-center gap-2">
                  {rules.special ? <FaCheck className="text-green-500" /> : <FaTimes className="text-red-500" />}
                  <span className={rules.special ? "text-green-600 dark:text-green-400" : ""}>At least 1 special character (@$!%*?&)</span>
                </div>
              </motion.div>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaLock className="text-gray-400" />
              </div>
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input-field pl-10 pr-10"
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                {showConfirmPassword ? <FaEyeSlash className="text-gray-400" /> : <FaEye className="text-gray-400" />}
              </button>
            </div>
            {confirmPassword.length > 0 && password !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
            )}
            {confirmPassword.length > 0 && password === confirmPassword && (
              <p className="text-xs text-green-500 mt-1 flex items-center gap-1"><FaCheck /> Passwords match</p>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || !isPasswordStrong}
            className={`btn-primary w-full flex items-center justify-center gap-2 ${
              (!isPasswordStrong || loading) ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              'Reset Password'
            )}
          </button>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;
